import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { MortgageFormData } from "../../../types/mortgage";
import { toast } from "sonner";
import { logger } from "../../../lib/logger";

export function useResultsSaving() {
  const saveCalculationResult = useMutation(api.calculationResults.saveCalculationResults);

  const saveResults = async (
    formData: MortgageFormData,
    maxLoanData: any,
    householdExpenditure: any
  ) => {
    if (!maxLoanData || !householdExpenditure) {
      logger.warn("Attempted to save results without complete data", { 
        hasMaxLoanData: !!maxLoanData,
        hasHouseholdExpenditure: !!householdExpenditure 
      });
      return;
    }
    
    // Validate required calculation results
    if (typeof maxLoanData.maxLoan5YearFixed !== 'number' || typeof maxLoanData.maxLoanOther !== 'number') {
      logger.warn("Invalid maxLoanData structure", { maxLoanData });
      return;
    }

    try {
      logger.info("Saving calculation results", { 
        hasMaxLoanData: !!maxLoanData,
        hasHouseholdExpenditure: !!householdExpenditure 
      });
      
      const saveData = {
        // Contact Information (undefined for anonymous users without contact details)
        name: formData.contact.name || undefined,
        email: formData.contact.email || undefined,
        telephone: formData.contact.telephone || undefined,
        // Property and Loan Details
        loanType: formData.property.loanType,
        propertyValue: formData.property.propertyValue,
        loanRequired: formData.property.loanRequired,
        loanTermYears: Math.round(formData.property.loanTermMonths / 12),
        payRate: formData.property.payRate,
        fiveYearFixedTerm: formData.property.fiveYearFixedTerm,
        repaymentType: formData.property.repaymentType,
        amountInterestOnly: formData.property.amountInterestOnly,
        // Income Details
        totalIncomeNetOfTax: formData.income.totalIncomeNetOfTax,
        incomeFromProperties: formData.income.incomeFromProperties,
        foreignIncome: formData.income.foreignIncome,
        foreignCurrency: formData.income.foreignCurrency,
        // Household Details
        adults: formData.household.adults,
        children: formData.household.children,
        totalHouseholdExpenditure: typeof householdExpenditure === 'number' ? householdExpenditure : householdExpenditure?.totalMonthly || 0,
        // Commitments
        creditCommitments: formData.expenditure.commitments.creditCommitments,
        additionalCommitments: formData.expenditure.commitments.additionalCommitments,
        pensionContributions: formData.expenditure.commitments.pensionContributions,
        otherMortgages: formData.expenditure.commitments.otherMortgages,
        // Background Properties
        btlTotalValueOfBorrowing: formData.background.btlTotalValueOfBorrowing,
        otherTotalValueOfBorrowing: formData.background.otherTotalValueOfBorrowing,
        // Calculation Results
        maxLoan5Year: maxLoanData.maxLoan5YearFixed,
        maxLoanOther: maxLoanData.maxLoanOther,
      };
      
      logger.info("Calling saveCalculationResult mutation", { 
        loanType: saveData.loanType,
        hasMaxLoan5Year: typeof saveData.maxLoan5Year === 'number',
        hasMaxLoanOther: typeof saveData.maxLoanOther === 'number'
      });
      
      const result = await saveCalculationResult(saveData);
      
      logger.info("Save mutation completed", { result });
      toast.success("Calculation results saved successfully!");
      
      // Track successful calculation save
      logger.calculationSaved('auto-saved');
    } catch (error) {
      logger.error("Failed to save calculation results", error as Error);
      console.error("Save calculation results error:", error);
      toast.error("Failed to save calculation results");
      throw error; // Re-throw to allow caller to handle
    }
  };

  return { saveResults };
}
